<template>
    <v-card class="approval-modal-premium overflow-hidden">
        <!-- Header with Gradient -->
        <v-toolbar flat dark class="premium-header">
            <v-toolbar-title class="headline font-weight-light">
                <v-icon left size="28" class="mr-2">fas fa-user-shield</v-icon>
                Approval <span class="font-weight-bold">Layanan Pelanggan</span>
            </v-toolbar-title>
        </v-toolbar>

        <!-- Main Content Area -->
        <v-card-text class="pa-0 grey lighten-5">
            <div v-if="loading" class="text-center py-12">
                <v-progress-circular indeterminate color="primary" size="64" width="6"></v-progress-circular>
                <p class="mt-4 grey--text text--darken-1 font-weight-medium">Sinkronisasi data teknis...</p>
            </div>
            
            <div v-else-if="!detail" class="pa-8">
                <v-alert prominent type="error" border="left" elevation="2">
                    <v-row align="center">
                        <v-col class="grow">
                            Konfigurasi internet untuk pelanggan ini belum lengkap atau tidak ditemukan.
                        </v-col>
                        <v-col class="shrink">
                            <v-btn small text @click="close">Tutup</v-btn>
                        </v-col>
                    </v-row>
                </v-alert>
            </div>

            <template v-else>
                <!-- Quick Summary Bar -->
                <div class="summary-bar px-6 py-4 white border-bottom">
                    <v-row align="center" dense>
                        <v-col cols="12" md="6">
                            <div class="d-flex align-center">
                                <v-avatar color="primary lighten-4" size="48" class="mr-4">
                                    <v-icon color="primary">fas fa-user</v-icon>
                                </v-avatar>
                                <div>
                                    <div class="headline font-weight-bold primary--text mb-0">{{ detail.nama }}</div>
                                    <div class="caption grey--text text--darken-1">ID: {{ detail.id }} • {{ detail.hp }}</div>
                                </div>
                            </div>
                        </v-col>
                        <v-col cols="12" md="6" class="text-md-right">
                            <v-chip color="orange lighten-5" text-color="orange darken-3" class="font-weight-bold mr-2" label>
                                <v-icon x-small left>fas fa-clock</v-icon> MENUNGGU APPROVAL
                            </v-chip>
                            <v-chip color="primary lighten-5" text-color="primary darken-2" class="font-weight-bold" label>
                                <v-icon x-small left>fas fa-building</v-icon> {{ detail.kantor_name }}
                            </v-chip>
                        </v-col>
                    </v-row>
                </div>

                <!-- Tabs for Details -->
                <v-tabs v-model="tab" color="primary" class="detail-tabs border-bottom" height="60">
                    <v-tab href="#info" class="text-none">
                        <v-icon small left>fas fa-id-card</v-icon> Profil & Lokasi
                    </v-tab>
                    <v-tab href="#teknis" class="text-none">
                        <v-icon small left>fas fa-network-wired</v-icon> Teknis & Biaya
                    </v-tab>
                    <v-tab href="#survey" class="text-none">
                        <v-icon small left>fas fa-camera-retro</v-icon> Hasil Survey
                    </v-tab>
                </v-tabs>

                <v-tabs-items v-model="tab" class="transparent scrollable-content">
                    <!-- TAB 1: PROFIL & LOKASI -->
                    <v-tab-item value="info" class="pa-6">
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-card elevation="0" class="transparent">
                                    <div class="section-title mb-4">Informasi Identitas</div>
                                    <v-card-text class="pa-0 pt-2">
                                        <v-row dense>
                                            <v-col cols="12" sm="8">
                                                <v-list dense class="transparent pa-0">
                                                    <v-list-item class="px-0 min-h-auto mb-2">
                                                        <v-list-item-content class="py-0">
                                                            <div class="caption grey--text">Email Address</div>
                                                            <div class="body-2 font-weight-medium">{{ detail.email || '-' }}</div>
                                                        </v-list-item-content>
                                                    </v-list-item>
                                                    <v-list-item class="px-0 min-h-auto mb-2">
                                                        <v-list-item-content class="py-0">
                                                            <div class="caption grey--text">Status Hubungan</div>
                                                            <div class="body-2 font-weight-medium">{{ detail.status_hub || '-' }}</div>
                                                        </v-list-item-content>
                                                    </v-list-item>
                                                    <v-list-item class="px-0 min-h-auto">
                                                        <v-list-item-content class="py-0">
                                                            <div class="caption grey--text">Alamat Sesuai KTP</div>
                                                            <div class="body-2">{{ detail.alamat }}</div>
                                                            <div class="caption grey--text mt-1">
                                                                {{ detail.kecamatan_name }}, {{ detail.kabupaten_name }}, {{ detail.provinsi_name }}
                                                            </div>
                                                        </v-list-item-content>
                                                    </v-list-item>
                                                </v-list>
                                            </v-col>
                                            <v-col cols="12" sm="4" class="text-center">
                                                <div class="caption grey--text mb-1">Foto KTP</div>
                                                <v-hover v-slot="{ hover }">
                                                    <v-card flat outlined class="ktp-preview-card" @click="viewImage(detail.url_ktp)">
                                                        <v-img v-if="detail.url_ktp" :src="getImageUrl(detail.url_ktp)" aspect-ratio="1.5" class="grey lighten-4 rounded">
                                                            <v-overlay v-if="hover" absolute color="primary" opacity="0.4">
                                                                <v-icon small>fas fa-search-plus</v-icon>
                                                            </v-overlay>
                                                        </v-img>
                                                        <div v-else class="fill-height d-flex align-center justify-center grey lighten-4" style="min-height: 80px">
                                                            <v-icon color="grey lighten-2">fas fa-image</v-icon>
                                                        </div>
                                                    </v-card>
                                                </v-hover>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-card elevation="0" class="transparent border-left-dashed pl-md-6">
                                    <div class="section-title mb-4">Lokasi Instalasi</div>
                                    <v-list dense class="transparent pa-0">
                                        <v-list-item class="px-0 min-h-auto mb-2">
                                            <v-list-item-content class="py-0">
                                                <div class="caption grey--text">Alamat Pemasangan</div>
                                                <div class="body-2 font-weight-medium">{{ detail.alamat_pasang }}</div>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-list-item class="px-0 min-h-auto mb-2">
                                            <v-list-item-content class="py-0">
                                                <div class="caption grey--text">Koordinat Geografis</div>
                                                <v-chip x-small color="blue lighten-5" text-color="blue darken-3" class="mt-1">
                                                    <v-icon x-small left>fas fa-map-marker-alt</v-icon>
                                                    {{ detail.latitude }}, {{ detail.longitude }}
                                                </v-chip>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-list-item class="px-0 min-h-auto">
                                            <v-list-item-content class="py-0">
                                                <div class="caption grey--text">Status Kepemilikan Tempat</div>
                                                <div class="body-2 font-weight-medium">
                                                    <v-icon small left>{{ detail.status_tempat == 1 ? 'fas fa-home' : 'fas fa-file-contract' }}</v-icon>
                                                    {{ detail.status_tempat == 1 ? 'Milik Sendiri' : 'Sewa / Lainnya' }}
                                                </div>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-list>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-tab-item>

                    <!-- TAB 2: TEKNIS & BIAYA -->
                    <v-tab-item value="teknis" class="pa-6">
                        <v-row>
                            <!-- Teknis PPPoE -->
                            <v-col cols="12" md="7">
                                <v-card outlined class="premium-inner-card mb-4">
                                    <div class="card-header blue darken-3 white--text px-4 py-2 subtitle-2">
                                        Konfigurasi PPPoE Radius
                                    </div>
                                    <v-card-text>
                                        <v-row dense>
                                            <v-col cols="12" sm="6" class="mb-3">
                                                <div class="caption grey--text">Paket Layanan</div>
                                                <div class="body-1 font-weight-bold success--text">{{ detail.paket_name }}</div>
                                            </v-col>
                                            <v-col cols="12" sm="6" class="mb-3">
                                                <div class="caption grey--text">Router Gateway</div>
                                                <div class="body-2 font-weight-medium">{{ detail.router_name || '-' }}</div>
                                            </v-col>
                                            <v-col cols="12" sm="6">
                                                <div class="caption grey--text">Username</div>
                                                <code class="radius-code">{{ detail.username }}</code>
                                            </v-col>
                                            <v-col cols="12" sm="6">
                                                <div class="caption grey--text">Password</div>
                                                <code class="radius-code">{{ detail.password }}</code>
                                            </v-col>
                                        </v-row>
                                        <v-divider class="my-4"></v-divider>
                                        <v-row dense>
                                            <v-col cols="4">
                                                <div class="caption grey--text text-center">Tipe</div>
                                                <div class="body-2 font-weight-bold text-center">{{ detail.tipe_langganan == 1 ? 'Personal' : 'Corporate' }}</div>
                                            </v-col>
                                            <v-col cols="4" class="border-left">
                                                <div class="caption grey--text text-center">IP Address</div>
                                                <div class="body-2 font-weight-bold text-center">{{ detail.ip_address || 'DHCP/Dynamic' }}</div>
                                            </v-col>
                                            <v-col cols="4" class="border-left">
                                                <div class="caption grey--text text-center">VLAN ID</div>
                                                <div class="body-2 font-weight-bold text-center">{{ detail.vlan_id || '-' }}</div>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>
                            </v-col>

                            <!-- Finansial -->
                            <v-col cols="12" md="5">
                                <v-card outlined class="premium-inner-card border-left-orange">
                                    <div class="card-header orange darken-3 white--text px-4 py-2 subtitle-2">
                                        Rincian Finansial & Pajak
                                    </div>
                                    <v-card-text class="pa-4">
                                        <div class="d-flex justify-space-between mb-2">
                                            <span class="body-2 grey--text">Harga Paket</span>
                                            <span class="body-2 font-weight-bold primary--text text-h6">Rp {{ parseInt(detail.harga).toLocaleString('id-ID') }}</span>
                                        </div>
                                        <v-divider class="mb-3"></v-divider>
                                        <div class="d-flex justify-space-between mb-1">
                                            <span class="caption grey--text">PPN ({{ detail.ppn }}%)</span>
                                            <span class="caption font-weight-medium">Rp {{ parseInt(detail.ppn_rp || 0).toLocaleString('id-ID') }}</span>
                                        </div>
                                        <div class="d-flex justify-space-between mb-1">
                                            <span class="caption grey--text">Potongan PPH23</span>
                                            <span class="caption font-weight-medium" :class="detail.potong_pph23 ? 'red--text' : ''">
                                                {{ detail.pph23 }}% {{ detail.potong_pph23 ? '(Aktif)' : '' }}
                                            </span>
                                        </div>
                                        <div class="d-flex justify-space-between mb-1">
                                            <span class="caption grey--text">Potongan Zakat</span>
                                            <span class="caption font-weight-medium" :class="detail.potong_zakat ? 'red--text' : ''">
                                                {{ detail.zakat }}% {{ detail.potong_zakat ? '(Aktif)' : '' }}
                                            </span>
                                        </div>
                                        <div class="d-flex justify-space-between mb-1">
                                            <span class="caption grey--text">Kickback (Saled)</span>
                                            <span class="caption font-weight-medium">Rp {{ parseInt(detail.kickback || 0).toLocaleString('id-ID') }}</span>
                                        </div>
                                        <div class="d-flex justify-space-between">
                                            <span class="caption grey--text">Diskon Khusus</span>
                                            <span class="caption font-weight-bold success--text">- Rp {{ parseInt(detail.diskon || 0).toLocaleString('id-ID') }}</span>
                                        </div>
                                        
                                        <div class="mt-4 pa-2 blue-grey lighten-5 rounded text-center">
                                            <div class="caption grey--text">Service Level Agreement (SLA)</div>
                                            <div class="body-1 font-weight-black blue-grey--text text--darken-3">{{ detail.sla }}%</div>
                                        </div>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-tab-item>

                    <!-- TAB 3: HASIL SURVEY -->
                    <v-tab-item value="survey" class="pa-6">
                        <v-row>
                            <v-col cols="12" md="4">
                                <v-card flat class="transparent">
                                    <div class="section-title mb-4">Data Lapangan</div>
                                    <div class="survey-metric mb-4">
                                        <div class="caption grey--text">Surveyor Penugasan</div>
                                        <div class="body-1 font-weight-bold">{{ detail.surveyor || '-' }}</div>
                                    </div>
                                    <div class="survey-metric mb-4">
                                        <div class="caption grey--text">Waktu Survey</div>
                                        <div class="body-2 font-weight-medium">
                                            <v-icon small left>far fa-calendar-alt</v-icon>
                                            {{ detail.date_survey | formatDate }}
                                        </div>
                                    </div>
                                    <v-alert colored-border border="left" color="green" icon="fas fa-check-circle" class="caption py-2 mt-4">
                                        Lokasi telah diverifikasi dan siap untuk dilakukan instalasi perangkat.
                                    </v-alert>
                                </v-card>
                            </v-col>
                            <v-col cols="12" md="8">
                                <div class="section-title mb-4">Dokumentasi Visual</div>
                                <v-row dense>
                                    <v-col v-for="(img, i) in [detail.image_1, detail.image_2, detail.image_3]" :key="i" cols="4">
                                        <v-hover v-slot="{ hover }">
                                            <v-card flat outlined class="image-preview-card" @click="viewImage(img)">
                                                <v-img v-if="img" :src="getImageUrl(img)" aspect-ratio="1" class="grey lighten-3">
                                                    <v-overlay v-if="hover" absolute color="primary" opacity="0.4">
                                                        <v-icon large>fas fa-search-plus</v-icon>
                                                    </v-overlay>
                                                    <template v-slot:placeholder>
                                                        <v-row class="fill-height ma-0" align="center" justify="center">
                                                            <v-progress-circular indeterminate color="primary lighten-3"></v-progress-circular>
                                                        </v-row>
                                                    </template>
                                                </v-img>
                                                <div v-else class="fill-height d-flex align-center justify-center grey lighten-4" style="min-height: 160px">
                                                    <v-icon color="grey lighten-2" size="48">fas fa-image</v-icon>
                                                </div>
                                            </v-card>
                                        </v-hover>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>
                    </v-tab-item>
                </v-tabs-items>
            </template>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-6 white">
            <v-btn text color="grey darken-1" class="text-none font-weight-bold px-6" @click="close">Tutup</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="success" x-large :loading="submitting" :disabled="loading || !detail" @click="confirmApproval" class="text-none font-weight-black px-10 elevation-4">
                <v-icon left>fas fa-check-circle</v-icon>
                KONFIRMASI & AKTIFKAN LAYANAN
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import { EventBus } from './../../event-bus'
import moment from 'moment'

export default {
    props: ['id'],
    data() {
        return {
            tab: null,
            loading: true,
            submitting: false,
            detail: null
        }
    },
    filters: {
        formatDate(val) {
            if (!val) return '-';
            return moment(val).format('DD MMMM YYYY • HH:mm');
        }
    },
    methods: {
        fetchDetail() {
            this.loading = true;
            axios.get(`api/pelanggan/approval-detail/${this.id}`)
                .then(res => {
                    this.detail = res.data;
                })
                .catch(err => {
                    console.error(err);
                    Toast.fire({ icon: 'error', title: 'Sinkronisasi data gagal' });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        getImageUrl(path) {
            if (!path) return '';
            // Mengambil base URL dari environment variable MIX_PORTAL_URL
            const portalUrl = process.env.MIX_PORTAL_URL || 'http://localhost:3031';
            return path.startsWith('http') ? path : `${portalUrl}${path}`;
        },
        viewImage(url) {
            if (!url) return;
            window.open(this.getImageUrl(url), '_blank');
        },
        confirmApproval() {
            Swal.fire({
                title: 'Konfirmasi Approval',
                html: `Anda akan mengaktifkan layanan untuk <strong>${this.detail.nama}</strong>.<br><small class="grey--text">Akun PPPoE akan segera didaftarkan ke Radius server.</small>`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#28a745',
                cancelButtonColor: '#757575',
                confirmButtonText: 'Ya, Setujui Sekarang',
                cancelButtonText: 'Batal',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.submitting = true;
                    axios.post(`api/pelanggan/approve`, { id: this.id })
                        .then(res => {
                            Swal.fire({
                                title: 'Layanan Aktif!',
                                text: 'Pelanggan telah berhasil dipindahkan ke daftar aktif.',
                                icon: 'success',
                                confirmButtonColor: '#2196F3'
                            });
                            EventBus.$emit('pelanggan:updated');
                            this.close();
                        })
                        .catch(err => {
                            Swal.fire('Gagal!', err.response.data.message || 'Terjadi kesalahan sistem.', 'error');
                        })
                        .finally(() => {
                            this.submitting = false;
                        });
                }
            })
        },
        close() {
            EventBus.$emit('modal:close');
        }
    },
    mounted() {
        this.fetchDetail();
    }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

.approval-modal-premium {
    font-family: 'Inter', sans-serif !important;
    border-radius: 16px !important;
}

.premium-header {
    background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%) !important;
    height: 70px !important;
}

.summary-bar {
    background-color: #fff;
    box-shadow: 0 4px 12px rgba(0,0,0,0.02);
}

.detail-tabs {
    background-color: #fff;
}

.section-title {
    font-size: 0.85rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #1a237e;
    border-bottom: 2px solid #e8eaf6;
    padding-bottom: 8px;
}

.detail-table {
    width: 100%;
}

.min-h-auto {
    min-height: unset !important;
}

.border-left-dashed {
    border-left: 1px dashed #e0e0e0;
}

.border-bottom {
    border-bottom: 1px solid #eee;
}

.premium-inner-card {
    border-radius: 12px !important;
    overflow: hidden;
    background-color: #fff !important;
    transition: transform 0.2s ease;
}

.premium-inner-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(0,0,0,0.05) !important;
}

.border-left-orange {
    border-left: 5px solid #ef6c00 !important;
}

.radius-code {
    background: #f1f3f4;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 0.9rem;
    display: block;
    margin-top: 4px;
    color: #d81b60;
    font-family: 'Fira Code', monospace !important;
}

.image-preview-card {
    border-radius: 10px !important;
    cursor: pointer;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.image-preview-card:hover {
    border-color: #2196F3 !important;
}

.ktp-preview-card {
    border-radius: 8px !important;
    cursor: pointer;
    overflow: hidden;
    transition: all 0.3s ease;
    border: 1px dashed #ccc !important;
}

.ktp-preview-card:hover {
    border-color: #2196F3 !important;
    border-style: solid !important;
}

.border-left {
    border-left: 1px solid #eee;
}

.scrollable-content {
    max-height: 55vh;
    overflow-y: auto;
}

/* Custom Scrollbar */
.scrollable-content::-webkit-scrollbar {
    width: 6px;
}
.scrollable-content::-webkit-scrollbar-track {
    background: #f1f1f1;
}
.scrollable-content::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 10px;
}
.scrollable-content::-webkit-scrollbar-thumb:hover {
    background: #999;
}
</style>

<style>
/* Global styles to ensure SweetAlert is always on top of Vuetify modal */
.swal2-container {
    z-index: 10000 !important;
}
</style>
